import type { BuyUniverseFieldKeys } from '@moment/trading-types/fields'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'
import type { TargetBandState } from '@/components/molecules/target-band'
import type {
  AllocationConstraintState,
  ModelBuilderState,
} from '@/reducers/model-builder'
import type {
  ModelDetailResponse,
  ModelPreviewRequest,
} from '@/types/api/models'

import { MaturityOption } from '@moment/horizon-data-access/proposal'
import {
  type FilterBuilderResponseUnion,
  type FilterValue,
  Operator,
} from '@moment/trading-types/filter'
import {
  CompoundExpressionOperator,
  type FilterCondition,
  type FilterObject,
} from '@moment/trading-types/filter-expression'
import {
  BondType,
  type CouponType,
  type TreasurySubtype,
} from '@moment/trading-types/reference-data'
import { Decimal } from 'decimal.js-light'
import { z } from 'zod'

import {
  isValidAllocation,
  isValidCondition,
  isValidTargetBand,
} from '@/components/molecules/constraint-list/helpers'
import {
  type DiversificationConstraintValue,
  DiversificationMode,
  type TieredDiversificationTier,
} from '@/components/molecules/diversification-constraint/types'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { createLogger } from '@/lib/logger'
import { getLadderPeriods } from '@/portfolio/proposal-builder/data/transformers/ladder-to-constraints'
import { stripNoOpScope } from '@/portfolio/shared/constraint/no-op-scope'
import { getFieldMetadata, type ModelBuilder } from '@/reducers/model-builder'
import {
  type AllocationConstraint,
  type AverageValueConstraint,
  type BuyUniverseConstraint,
  type ConstraintFilterCondition,
  type DiversificationConstraint,
  type LadderPolicyConstraint,
  type ModelConstraint,
  ModelConstraintType,
  type Piece,
  type PiecewiseCondition,
  type PiecewiseConstraint,
  type UniverseConstraint,
} from '@/types/api/constraints'
import { RatingMode } from '@/types/enums/models'
import { TargetBandMode } from '@/types/enums/target-band.mode'
import { decimalToPercentage } from '@/utils/presentation/formatting'
import {
  generateAssignmentDate,
  generateAssignmentDateNextYear,
} from '@/utils/sleeve-management'

import { AllowBlockRequireEnum, AllowEnum } from './allow-map'
import {
  createBuyUniverseConstraintFilters,
  createUniverseConstraintFilters,
} from './create-universe-constraint-filters'
import {
  BucketType,
  DEFAULT_MODEL_BUILDER_VALUES,
  DEFAULT_WEIGHT_TOLERANCE,
} from './default-values'
import { DiversificationApiField } from './diversification-api-field'
import {
  DIVERSIFICATION_KEYS_TO_API_FIELD,
  DIVERSIFICATION_SLIDER_DEFAULTS,
  type DiversificationConstraintModelBuilderStoreValues,
  type DiversificationKeys,
  nonApplicableDiversificationFields,
} from './diversification-tab'
import { sliderConfigs } from './model-setup-tab'
import { MunicipalStatePreference } from './municipal-state-preference'
import {
  nonApplicableTargetAverageFields,
  TARGET_AVERAGE_KEYS_TO_API_FIELD,
  TargetAverageApiField,
  type TargetAverageKeys,
} from './target-averages-tab'
import {
  API_FIELD_TO_UNIVERSE_KEYS,
  nonApplicableUniverseFields,
  type SliderKeys,
  UniverseApiField,
  type UniverseKeys,
} from './universe-tab'

// Create a logger instance for this module
const LOGGER = createLogger('components/pages/model-builder/helpers')

// Constants
const DEFAULT_CASH = 1_000_000
const DEFAULT_CASH_BUFFER = 0 // Always default to 0
const DEFAULT_SCHEMA_VERSION = 1
const DEFAULT_STATE = 'MO'

type LadderPolicyModelBuilderStoreValues = Pick<
  ModelBuilder,
  | 'rangeInPeriods'
  | 'bucketType'
  | 'useCallAdjustedMaturities'
  | 'bucketWeightBuffer'
  | 'ladderMarketValueTolerance'
>

// TODO These types need to be better integrated with filters and constraints
// For now, must stay in sync with both averageValueFields in this field and TargetAverageKeys
export enum TargetBandType {
  // Reference Data Fields
  Coupon = 'coupon',
  Duration = 'duration',
  LiquidityScore = 'liquidity_institutional_aggregate',
  SpRating = 'sp_rating',
  LowestRating = 'lowest_rating',
  MoodysRating = 'moodys_rating',
  Convexity = 'convexity',
  Maturity = 'maturity_in_years',
  // Buy Universe Constraints
  NextCallInYears = 'next_call_in_years',
  WorkoutInYears = 'workout_in_years',
}

/**
 * Utility: Create a range filter condition
 */
export const createRangeFilter = (
  field: string,
  range?: number[] | null,
  defaultRange?: number[],
): Record<string, ConstraintFilterCondition> | undefined => {
  if (!range?.length) return undefined

  const [min, max] = range

  // If we have default values, only include values that differ from defaults
  if (defaultRange) {
    const [defaultMin, defaultMax] = defaultRange
    const isMinDifferent = min !== defaultMin
    const isMaxDifferent = max !== defaultMax

    // If neither value is different from default, return undefined
    if (!isMinDifferent && !isMaxDifferent) return undefined

    const filterCondition: ConstraintFilterCondition = {}

    if (isMinDifferent) {
      filterCondition.greater_than_or_equal_to = min
    }

    if (isMaxDifferent) {
      filterCondition.less_than_or_equal_to = max
    }

    return { [field]: filterCondition }
  }

  // If no defaults provided, include both values if they exist
  return {
    [field]:
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      min! > 0
        ? { less_than_or_equal_to: max, greater_than_or_equal_to: min }
        : { less_than_or_equal_to: max },
  }
}

/**
 * Convert numeric value to percentage
 */
function toPercentage(value: number): number {
  return value / 100
}

/**
 * Generate constraints for a model based on dirty fields
 */
export const generateConstraints = (
  model: ModelBuilder,
  state: ModelBuilderState,
): ModelConstraint[] => {
  const constraints: ModelConstraint[] = []

  // Ladder policy constraint
  if (model.rangeInPeriods?.length === 2) {
    constraints.push(createLadderPolicyConstraint(model))
  }

  // Handle filter lists separately for persistence (filter + separate scope)
  if (model.includeFilterList?.list) {
    const field = model.includeFilterList.list.field.toLowerCase()
    const filter: Record<string, ConstraintFilterCondition> = {
      [field]: {
        in_filter_list: model.includeFilterList.list.list_id,
      },
    }
    constraints.push({
      type: ModelConstraintType.Universe,
      filter,
      ...(model.includeFilterList.scope && {
        scope: model.includeFilterList.scope,
      }),
      ...(model.constraintIdMap?.filter_list_include && {
        id: model.constraintIdMap?.filter_list_include,
      }),
    })
  }

  if (model.excludeFilterList?.list) {
    const field = model.excludeFilterList.list.field.toLowerCase()
    const filter: Record<string, ConstraintFilterCondition> = {
      [field]: {
        not_in_filter_list: model.excludeFilterList.list.list_id,
      },
    }
    constraints.push({
      type: ModelConstraintType.Universe,
      filter,
      ...(model.excludeFilterList.scope && {
        scope: model.excludeFilterList.scope,
      }),
      ...(model.constraintIdMap?.filter_list_exclude && {
        id: model.constraintIdMap?.filter_list_exclude,
      }),
    })
  }

  // Universe constraints (separated by type) - exclude filter lists as they're handled above
  const universeConstraints = createUniverseConstraintFilters(model)
  for (const filter of universeConstraints) {
    // Skip filter list constraints as they're handled separately above
    const filterKeys = Object.keys(filter)
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    const apiField = filterKeys[0]!
    const filterCondition = filter[apiField]
    const isFilterList =
      filterCondition &&
      typeof filterCondition === 'object' &&
      ('in_filter_list' in filterCondition ||
        'not_in_filter_list' in filterCondition)

    if (isFilterList) {
      continue
    }

    // Determine scope based on the single field in the filter
    let scope: FilterObject | undefined

    // Skip the 'type' field itself as it already defines asset classes
    if (apiField && apiField !== 'type') {
      // Map API field to ModelBuilder field name
      const modelBuilderField =
        API_FIELD_TO_UNIVERSE_KEYS[apiField as UniverseApiField]
      if (modelBuilderField) {
        scope = getScopeForField(
          modelBuilderField,
          'universe',
          model.assetClasses,
        )
      }
      // Note: Filter list fields and other special fields might not be in the mapping
      // They typically don't need scoping as they apply to all asset classes
    }

    const universeId = model.constraintIdMap?.[`universe:${apiField}`]
    constraints.push({
      type: ModelConstraintType.Universe,
      filter,
      ...(scope && { scope }),
      ...(universeId && { id: universeId }),
    })
  }

  // Buy universe constraints (separated by type)
  const buyUniverseConstraints = createSeparateBuyUniverseConstraints(model)
  constraints.push(...buyUniverseConstraints)

  // State allocation constraint
  const stateAllocationConstraint = createStateAllocationConstraint(
    model,
    state,
  )
  if (stateAllocationConstraint) constraints.push(stateAllocationConstraint)

  // Diversification constraints
  const diversificationValues: DiversificationConstraintModelBuilderStoreValues =
    {
      weightPerIssuer: model.weightPerIssuer,
      weightPerSector: model.weightPerSector,
      weightPerBond: model.weightPerBond,
      weightPerState: model.weightPerState,
      weightPerMaturityYear: model.weightPerMaturityYear,
      weightPerFdicCode: model.weightPerFdicCode,
      weightPerInsurer: model.weightPerInsurer,
      weightPerUltimateBorrower: model.weightPerUltimateBorrower,
      weightPerUltimateParent: model.weightPerUltimateParent,
    }
  Object.entries(diversificationValues).forEach(([field, value]) => {
    if (!value) return

    try {
      const sliderDefaults =
        DIVERSIFICATION_SLIDER_DEFAULTS[field as DiversificationKeys]
      const defaultMin = sliderDefaults.min
      const defaultMax = sliderDefaults.max

      // Check if the value is in tiered mode
      if (value.mode === DiversificationMode.Tiered) {
        const constraint = createDiversificationConstraintOrPiecewise(
          DIVERSIFICATION_KEYS_TO_API_FIELD[field as DiversificationKeys],
          value,
          model.assetClasses,
        )
        if (constraint) constraints.push(constraint)
        return
      }

      // Default mode: check if values have changed from defaults
      const hasMinChanged = value.weightRange[0] !== defaultMin
      const hasMaxChanged = value.weightRange[1] !== defaultMax

      if (
        hasMinChanged ||
        hasMaxChanged ||
        (field === 'weightPerFdicCode' &&
          model.assetClasses.includes(BondType.CD))
      ) {
        const constraint = createDiversificationConstraintOrPiecewise(
          DIVERSIFICATION_KEYS_TO_API_FIELD[field as DiversificationKeys],
          value,
          model.assetClasses,
        )
        if (constraint) constraints.push(constraint)
      }
    } catch {
      // Skip this entry if there's any error accessing the data
      return
    }
  })

  // Average value constraints
  const averageValueFields = {
    [TargetBandType.SpRating]: model.spRatingTargetBand,
    [TargetBandType.LowestRating]: model.lowestRatingTargetBand,
    [TargetBandType.MoodysRating]: model.moodysRatingTargetBand,
    [TargetBandType.Coupon]: model.couponTargetBand,
    [TargetBandType.Duration]: model.durationTargetBand,
    [TargetBandType.LiquidityScore]: model.liquidityScoreTargetBand,
    [TargetBandType.Convexity]: model.convexityTargetBand,
    [TargetBandType.Maturity]: model.maturityTargetBand,
  } as Record<TargetBandType, TargetBandState | null>

  Object.entries(averageValueFields)
    .filter(([, targetBand]) => isValidTargetBand(targetBand))
    .forEach(([field, targetBand]) => {
      if (targetBand) {
        const constraintId = model.constraintIdMap?.[`average_value:${field}`]
        const constraint = createTargetBandConstraint({
          field: field as TargetBandType,
          targetBand,
          assetClasses: model.assetClasses,
          constraintId,
        })
        if (constraint) constraints.push(constraint)
      }
    })

  if (model.allocationConstraints && model.allocationConstraints.length > 0) {
    const allocationConstraints = createAllocationConstraints(
      model.allocationConstraints,
    )
    constraints.push(...allocationConstraints)
  }

  return constraints
}

/**
 * Transform ModelBuilder to ModelPreviewRequest payload
 */
export const transformModelBuilderToPreviewPayload = (
  model: ModelBuilder,
  state: ModelBuilderState,
  riskGroupId: string,
  bucketType: BucketType,
): ModelPreviewRequest => ({
  cash: model.cash ?? DEFAULT_CASH,
  state: DEFAULT_STATE,
  risk_group_id: riskGroupId,
  strategy: {
    cash_buffer: DEFAULT_CASH_BUFFER,
    constraints: generateConstraints(model, state),
    schema_version: DEFAULT_SCHEMA_VERSION,
  },
  assignment_date:
    bucketType === BucketType.Months
      ? generateAssignmentDate()
      : generateAssignmentDateNextYear(),
})

/**
 * Create separate Buy Universe Constraints - each constraint contains only one type of filter
 */
const createSeparateBuyUniverseConstraints = (
  model: ModelBuilder,
): BuyUniverseConstraint[] => {
  const constraints: BuyUniverseConstraint[] = []
  const filters = createBuyUniverseConstraintFilters(model)

  for (const [key, filter] of Object.entries(filters)) {
    // Buy universe fields typically apply to all asset classes
    // But check if the field needs scoping (e.g., if it's a universe field)
    let scope: FilterObject | undefined

    // Map buy universe API field to universe field if applicable
    // Most buy universe fields don't need scoping, but we check for consistency
    const universeApiField = key as UniverseApiField
    const modelBuilderField = API_FIELD_TO_UNIVERSE_KEYS[universeApiField]
    if (modelBuilderField) {
      scope = getScopeForField(
        modelBuilderField,
        'universe',
        model.assetClasses,
      )
    }

    const buyUniverseId = model.constraintIdMap?.[`buy_universe:${key}`]
    constraints.push({
      type: ModelConstraintType.BuyUniverse,
      filter: {
        [key]: filter,
      },
      ...(scope && { scope }),
      ...(buyUniverseId && { id: buyUniverseId }),
    })
  }

  return constraints
}

/**
 * Create Ladder Policy Constraint
 */
const createLadderPolicyConstraint = (
  model: ModelBuilder,
): LadderPolicyConstraint => {
  const valuesToSend: LadderPolicyModelBuilderStoreValues = {
    rangeInPeriods: model.rangeInPeriods,
    bucketType: model.bucketType,
    useCallAdjustedMaturities: model.useCallAdjustedMaturities,
    bucketWeightBuffer: model.bucketWeightBuffer,
    ladderMarketValueTolerance: model.ladderMarketValueTolerance,
  }
  const {
    rangeInPeriods,
    useCallAdjustedMaturities,
    bucketWeightBuffer,
    ladderMarketValueTolerance,
  } = valuesToSend

  const periodLength = model.bucketWidth
  const weightTolerance = bucketWeightBuffer
    ? toPercentage(bucketWeightBuffer)
    : toPercentage(DEFAULT_WEIGHT_TOLERANCE)

  const constraint: LadderPolicyConstraint = {
    type: ModelConstraintType.LadderPolicy,
    period_length: periodLength,
    start_period: rangeInPeriods?.[0] ?? 0,
    end_period: rangeInPeriods?.[1] ?? 0,
    ...(bucketWeightBuffer == null && ladderMarketValueTolerance != null
      ? { market_value_tolerance: ladderMarketValueTolerance }
      : { weight_tolerance: weightTolerance }),
    use_call_adjusted_maturity: useCallAdjustedMaturities ?? false,
    ...(model.constraintIdMap?.ladder_policy && {
      id: model.constraintIdMap?.ladder_policy,
    }),
  }

  if (model.maturityOption) {
    constraint.maturity_option = model.maturityOption
    // keep for backwards compatibility
    constraint.use_call_adjusted_maturity =
      model.maturityOption === MaturityOption.workout_date
  }

  return constraint
}

/**
 * Create State Allocation Constraint
 */
const createStateAllocationConstraint = (
  model: ModelBuilder,
  state: ModelBuilderState,
): AllocationConstraint | null => {
  const {
    assetClasses,
    municipalStatePreference,
    municipalState,
    stateMinWeight,
    stateMaxWeight,
    stateTargetWeight,
  } = model

  const isMunicipal = assetClasses.includes(BondType.Municipal)
  const isStatePreferred =
    municipalStatePreference === MunicipalStatePreference.StatePreferred

  if (!isMunicipal || !isStatePreferred) return null

  // Check if any relevant fields are dirty
  const isAnyFieldDirty = [
    'municipalState',
    'stateMinWeight',
    'stateMaxWeight',
    'stateTargetWeight',
  ].some((field) => getFieldMetadata(state, field as keyof ModelBuilder).dirty)

  if (!isAnyFieldDirty) return null

  return {
    type: ModelConstraintType.Allocation,
    filter: {
      municipal_state: { equal_to: municipalState || '' },
    },
    min_weight: stateMinWeight ? toPercentage(stateMinWeight) : undefined,
    max_weight: stateMaxWeight ? toPercentage(stateMaxWeight) : undefined,
    target_weight: stateTargetWeight
      ? toPercentage(stateTargetWeight)
      : undefined,
    ...(model.constraintIdMap?.allocation_state && {
      id: model.constraintIdMap?.allocation_state,
    }),
  }
}

/**
 * Create Diversification Constraint
 */
const createDiversificationConstraint = ({
  field,
  weightRange,
  assetClasses,
  constraintId,
}: {
  field: DiversificationApiField
  weightRange?: number[] | null
  assetClasses?: BondType[]
  constraintId?: string
}): DiversificationConstraint | null => {
  if (!weightRange?.length) return null

  const [min, max] = weightRange

  // Map API field to ModelBuilder field name
  const modelBuilderField = Object.entries(
    DIVERSIFICATION_KEYS_TO_API_FIELD,
  ).find(([_, apiField]) => apiField === field)?.[0] as
    | DiversificationKeys
    | undefined

  const scope =
    modelBuilderField && assetClasses
      ? getScopeForField(modelBuilderField, 'diversification', assetClasses)
      : undefined

  const constraint: DiversificationConstraint = {
    type: ModelConstraintType.Diversification,
    field,
    ...(scope && { scope }),
    ...(constraintId && { id: constraintId }),
  }

  // Get the appropriate default values based on the field
  let defaultRange: number[]
  switch (field) {
    case DiversificationApiField.Issuer:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerIssuer.defaultValue
      break
    case DiversificationApiField.Sector:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerSector.defaultValue
      break
    case DiversificationApiField.Cusip:
      // For ISIN, treat [min, max] from slider defaults as the default for submission logic
      defaultRange = [
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerBond.min,
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerBond.max,
      ]
      break
    case DiversificationApiField.MunicipalState:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerState.defaultValue
      break
    case DiversificationApiField.MaturityYear:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerMaturityYear.defaultValue
      break
    case DiversificationApiField.FdicCode:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerFdicCode.defaultValue
      break
    case DiversificationApiField.Insurer:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerInsurer.defaultValue
      break
    case DiversificationApiField.UltimateBorrower:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateBorrower.defaultValue
      break
    case DiversificationApiField.UltimateParent:
      defaultRange =
        DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateParent.defaultValue
      break
    default: {
      const exhaustiveCheck: never = field
      throw new Error(`Unknown diversification field: ${exhaustiveCheck}`)
    }
  }

  const [defaultMin, defaultMax] = defaultRange
  const isMinDifferent = min !== defaultMin
  const isMaxDifferent = max !== defaultMax
  const isFdicField = field === DiversificationApiField.FdicCode

  // Handle FDIC field separately becuase it is a market value
  if (isFdicField) {
    if (isMinDifferent) {
      constraint.min_market_value_per_unique_value = min
      if (!isMaxDifferent) {
        constraint.max_market_value_per_unique_value = defaultMax
      }
    } else if (isMaxDifferent) {
      constraint.max_market_value_per_unique_value = max
    }

    // If neither are different, we still need to set the max
    else {
      constraint.max_market_value_per_unique_value = defaultMax
    }
  } else {
    if (isMinDifferent) {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      constraint.min_weight_per_unique_value = toPercentage(min!)
      // If max is the same as default, set it to max from slider defaults, since that means it's unconstrained
      if (!isMaxDifferent) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        constraint.max_weight_per_unique_value = toPercentage(defaultMax!)
      }
    }
    if (isMaxDifferent) {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      constraint.max_weight_per_unique_value = toPercentage(max!)
    }
  }

  // For ISIN, only skip if [min, max] from slider defaults
  const isWeightPerIsin = field === DiversificationApiField.Cusip
  const isDefaultIsin =
    isWeightPerIsin && min === defaultMin && max === defaultMax
  if (!isMinDifferent && !isMaxDifferent) {
    if (isDefaultIsin) {
      return null
    }
  }

  return constraint
}

/**
 * Create Piecewise Diversification Constraint for tiered mode
 */
const createPiecewiseDiversificationConstraint = ({
  field,
  tiers,
  assetClasses,
  constraintId,
}: {
  field: DiversificationApiField
  tiers: TieredDiversificationTier[]
  assetClasses?: BondType[]
  constraintId?: string
}): PiecewiseConstraint => {
  // Map API field to ModelBuilder field name for scope
  const modelBuilderField = Object.entries(
    DIVERSIFICATION_KEYS_TO_API_FIELD,
  ).find(([_, apiField]) => apiField === field)?.[0] as
    | DiversificationKeys
    | undefined

  const scope =
    modelBuilderField && assetClasses
      ? getScopeForField(modelBuilderField, 'diversification', assetClasses)
      : undefined

  const pieces: Piece[] = tiers.map((tier, index) => {
    const isLastTier = index === tiers.length - 1
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    const previousTierMax = index > 0 ? tiers[index - 1]!.maxMarketValue : 0

    const condition: PiecewiseCondition = {}

    if (!isLastTier && tier.maxMarketValue !== null) {
      // All tiers except last have upper bound
      condition.account_market_value = {
        less_than_or_equal_to: tier.maxMarketValue,
      }
      // Add lower bound if not first tier
      if (previousTierMax !== null && previousTierMax > 0) {
        condition.account_market_value.greater_than = previousTierMax
      }
    } else if (isLastTier && previousTierMax !== null) {
      // Last tier only has lower bound (catch-all)
      condition.account_market_value = {
        greater_than: previousTierMax,
      }
    }

    // Create the diversification constraint for this tier
    const diversificationConstraint = createDiversificationConstraint({
      field,
      weightRange: tier.weightRange,
      assetClasses,
    })

    return {
      condition,
      constraints: diversificationConstraint ? [diversificationConstraint] : [],
    }
  })

  return {
    type: ModelConstraintType.Piecewise,
    pieces,
    ...(scope && { scope }),
    ...(constraintId && { id: constraintId }),
  }
}

/**
 * Create Diversification Constraint or Piecewise Constraint based on mode
 */
const createDiversificationConstraintOrPiecewise = (
  field: DiversificationApiField,
  value: DiversificationConstraintValue | null,
  assetClasses?: BondType[],
): DiversificationConstraint | PiecewiseConstraint | null => {
  if (!value) return null

  if (value.mode === DiversificationMode.Default) {
    // Use existing createDiversificationConstraint logic
    return createDiversificationConstraint({
      field,
      weightRange: value.weightRange,
      assetClasses,
      constraintId: value.id,
    })
  }

  // Tiered mode: create PiecewiseConstraint
  return createPiecewiseDiversificationConstraint({
    field,
    tiers: value.tiers,
    assetClasses,
    constraintId: value.id,
  })
}

/**
 * Cleans an object by removing keys with unset values,
 * such as undefined or empty objects.
 * Suited for types with optional fields.
 *
 * @param obj - The object to clean.
 * @returns A new object without unset values.
 */

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
function removeUnsetKeys<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) =>
        value !== undefined ||
        (typeof value === 'object' &&
          value !== null &&
          Object.keys(value).length > 0),
    ),
  ) as T
}

const convertOperator = (o: Operator) => {
  switch (o) {
    case Operator.In:
      return 'in'
    case Operator.NotIn:
      return 'not_in'
    default:
      return o.toString()
  }
}

/**
 * Converts an array of conditions into a filter object.
 *
 * @param conditions - Array of conditions to be transformed. Each condition
 *   should have a 'field', 'operator', and 'inputValue'.
 * @returns A filter object mapping fields to their conditions.
 *
 * Example:
 * Input:
 * ```json
 *   [{
 *     "field": "type",
 *     "operator": "in",
 *     "inputValue": ["government"]
 *   },
 *   {
 *     "field": "treasury_subtype",
 *     "operator": "not_in",
 *     "inputValue": ["bill"]
 *   }]
 * ```
 *
 * Output:
 * ```json
 * {
 *   "type": {
 *     "in": ["government"]
 *   },
 *   "treasury_subtype": {
 *     "not_in": ["bill"]
 *   }
 * }
 * ```
 */
const conditionsToFilterObject = (
  conditions: AllocationConstraintState['conditions'],
): FilterCondition => {
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  const rawFilterIntermediary: Record<string, Record<string, any>> = {}

  const validConditions = conditions.filter(isValidCondition)

  if (validConditions.length === 0) {
    return {} as FilterCondition
  }

  validConditions.forEach((condition) => {
    const fieldKeyString = condition.field as string
    const operatorKeyEnum = condition.operator as Operator
    const currentInputValue = condition.inputValue
    const convertedOpStringKey = convertOperator(operatorKeyEnum)

    if (!rawFilterIntermediary[fieldKeyString]) {
      rawFilterIntermediary[fieldKeyString] = {}
    }
    rawFilterIntermediary[fieldKeyString][convertedOpStringKey] =
      currentInputValue
  })

  return rawFilterIntermediary as FilterCondition
}

const safeParseNumber = (value?: string): number | undefined => {
  if (!value) return undefined
  const parsed = parseFloat(value)
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  return isNaN(parsed) ? undefined : toPercentage(parsed)
}

const createAllocationConstraints = (
  constraints: AllocationConstraintState[],
): AllocationConstraint[] => {
  return constraints
    .filter(isValidAllocation)
    .map(({ id, targetBand, conditions }) => {
      const filter = conditionsToFilterObject(conditions)

      return removeUnsetKeys({
        type: ModelConstraintType.Allocation,
        filter,
        min_weight: safeParseNumber(targetBand?.min),
        max_weight: safeParseNumber(targetBand?.max),
        target_weight: safeParseNumber(targetBand?.target),
        ...(id && { id }),
      })
    })
}

const targetBandToValue = (
  field: TargetBandType,
  targetBand: TargetBandState,
  key: 'target' | 'min' | 'max',
): string | number | undefined => {
  const { mode, target, buffer } = targetBand

  if (
    field === TargetBandType.SpRating ||
    field === TargetBandType.LowestRating ||
    field === TargetBandType.MoodysRating
  )
    return targetBand[key] || undefined

  if (mode === TargetBandMode.MinMax) {
    const value = targetBand[key]
    if (value === '') return undefined
    const num = Number(value)
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    return isNaN(num) ? undefined : new Decimal(num).toNumber()
  }

  if (target === '') return undefined
  const targetNum = Number(target)
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  if (isNaN(targetNum)) return undefined
  const targetValue = new Decimal(targetNum)

  if (mode === TargetBandMode.Buffer) {
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    if (!buffer || isNaN(Number(buffer))) return undefined
    const bufferValue = new Decimal(Number(buffer))
    switch (key) {
      case 'min':
        return Math.max(0, targetValue.minus(bufferValue).toNumber())
      case 'max':
        return Math.max(0, targetValue.plus(bufferValue).toNumber())
      default:
        return targetValue.toNumber()
    }
  }

  return targetValue.toNumber()
}

const createTargetBandConstraint = ({
  field,
  targetBand,
  assetClasses,
  constraintId,
}: {
  field: TargetBandType
  targetBand: TargetBandState
  assetClasses?: BondType[]
  constraintId?: string
}): AverageValueConstraint => {
  // Map TargetBandType to ModelBuilder field name
  // TargetBandType enum values match TargetAverageApiField values
  const apiField = field as unknown as TargetAverageApiField
  const modelBuilderField = Object.entries(
    TARGET_AVERAGE_KEYS_TO_API_FIELD,
  ).find(([_, apiFieldValue]) => apiFieldValue === apiField)?.[0] as
    | TargetAverageKeys
    | undefined

  const scope =
    modelBuilderField && assetClasses
      ? getScopeForField(modelBuilderField, 'targetAverage', assetClasses)
      : undefined

  return {
    type: ModelConstraintType.AverageValue,
    field: apiField,
    min_average_value: targetBandToValue(field, targetBand, 'min'),
    target_average_value: targetBandToValue(field, targetBand, 'target'),
    max_average_value: targetBandToValue(field, targetBand, 'max'),
    ...(scope && { scope }),
    ...(constraintId && { id: constraintId }),
  }
}

/**
 * Transforms a ModelDetailResponse object into a ModelBuilder object
 */
export const transformModelDetailToBuilder = (
  modelDetail: ModelDetailResponse,
  isTemplate: string | undefined,
): ModelBuilder => {
  const { constraints } = modelDetail.strategy

  const model: ModelBuilder = {
    ...DEFAULT_MODEL_BUILDER_VALUES,
    id: modelDetail.id,
    name: isTemplate ? `${modelDetail.name} Copy` : modelDetail.name,
    description: modelDetail.description,
    assetClasses: modelDetail.asset_classes,
    sharing_organization_id: modelDetail.sharing_organization_id,
    allocationConstraints: [],
    constraintIdMap: {},
    cash: modelDetail.model_cash_amount ?? DEFAULT_CASH,
    // Absence of a constraint means unconstrained; the constraint loop overrides if present
    weightPerBond: null,
    // Absence of a constraint means Allow; the constraint loop overrides to DontAllow if present
    municipalTaxableFederal: [AllowEnum.Allow],
    amtApplicable: [AllowEnum.Allow],
    privatePlacement: [AllowEnum.Allow],
    regulationS: [AllowEnum.Allow],
    belowDeMinimisThreshold: [AllowEnum.Allow],
    fdicInsured: [AllowEnum.Allow],
    vrdoIndicator: [AllowBlockRequireEnum.Allow],
  }

  if (modelDetail.municipal_state) {
    model.municipalState = modelDetail.municipal_state
    model.municipalStatePreference = MunicipalStatePreference.StateFocused
  }

  // Check if ladder policy constraint exists
  const ladderPolicyConstraint = constraints.find(
    (constraint) => constraint.type === ModelConstraintType.LadderPolicy,
  ) as LadderPolicyConstraint | undefined

  // If no ladder policy constraint exists, set ladderPolicyEnabled to false and keep values null
  // This prevents overriding null values with defaults when editing existing models
  if (!ladderPolicyConstraint) {
    model.ladderPolicyEnabled = false
    model.rangeInPeriods = null
    model.bucketWeightBuffer = null
    model.ladderMarketValueTolerance = null
    model.useCallAdjustedMaturities =
      DEFAULT_MODEL_BUILDER_VALUES.useCallAdjustedMaturities
  }

  constraints.forEach((constraint) => {
    switch (constraint.type) {
      case ModelConstraintType.LadderPolicy: {
        const ladderPolicyValuesToSet: LadderPolicyModelBuilderStoreValues & {
          maturityOption?: MaturityOption
        } = {
          rangeInPeriods: [
            constraint.start_period !== undefined
              ? constraint.start_period
              : sliderConfigs.rangeInPeriods.min,
            constraint.end_period !== undefined ||
            constraint.num_periods !== undefined
              ? getLadderPeriods(constraint).end
              : sliderConfigs.rangeInPeriods.max,
          ],
          bucketType:
            constraint.period_length % 12 === 0
              ? BucketType.Years
              : BucketType.Months,
          useCallAdjustedMaturities:
            constraint.use_call_adjusted_maturity ??
            DEFAULT_MODEL_BUILDER_VALUES.useCallAdjustedMaturities,
          bucketWeightBuffer:
            constraint.weight_tolerance != null
              ? constraint.weight_tolerance * 100
              : null,
          ladderMarketValueTolerance: constraint.market_value_tolerance ?? null,
        }
        if (constraint.maturity_option) {
          ladderPolicyValuesToSet.maturityOption = constraint.maturity_option
        } else if (constraint.use_call_adjusted_maturity === true) {
          ladderPolicyValuesToSet.maturityOption = MaturityOption.workout_date
        }
        Object.assign(model, ladderPolicyValuesToSet)
        // Set bucketWidth to the actual period_length
        model.bucketWidth = constraint.period_length
        // Ensure ladderPolicyEnabled is true when we have a ladder policy
        model.ladderPolicyEnabled = true
        if (constraint.id && model.constraintIdMap) {
          model.constraintIdMap.ladder_policy = constraint.id
        }
        break
      }
      case ModelConstraintType.BuyUniverse:
      case ModelConstraintType.Universe: {
        const { filter: filterObject } = constraint
        const constraintTypePrefix =
          constraint.type === ModelConstraintType.BuyUniverse
            ? 'buy_universe'
            : 'universe'
        // Extract scope if this is a Universe constraint (BuyUniverse doesn't have scope)
        const scope =
          constraint.type === ModelConstraintType.Universe
            ? stripNoOpScope((constraint as UniverseConstraint).scope)
            : undefined
        const filter = Object.entries(filterObject).filter(
          removeOrAndKeysFromFilter,
        )
        // Store constraint ID for each filter field.
        // When a single API constraint has multiple filter fields, each field
        // becomes its own constraint on save. Append :{index} for index > 0
        // to avoid duplicate IDs in the PATCH payload.
        if (constraint.id && model.constraintIdMap) {
          for (const [index, [field]] of filter.entries()) {
            model.constraintIdMap[`${constraintTypePrefix}:${field}`] =
              index === 0 ? constraint.id : `${constraint.id}:${index}`
          }
        }
        filter.forEach(
          ([field, condition]: [
            string,
            Record<
              keyof ConstraintFilterCondition,
              number | string | boolean | string[]
            >,
          ]) => {
            if (Object.entries(condition).length === 0) return
            for (const [operator, value] of Object.entries(condition)) {
              // Handle filter list operators first
              if (operator === 'in_filter_list') {
                if (constraint.id && model.constraintIdMap) {
                  model.constraintIdMap.filter_list_include = constraint.id
                }
                model.includeFilterList = {
                  list: {
                    list_id: value as string,
                    field: field,
                    name: value as string, // Use list_id as fallback name since we don't have the actual name here
                  },
                  scope,
                }
                continue
              }
              if (operator === 'not_in_filter_list') {
                if (constraint.id && model.constraintIdMap) {
                  model.constraintIdMap.filter_list_exclude = constraint.id
                }
                model.excludeFilterList = {
                  list: {
                    list_id: value as string,
                    field: field,
                    name: value as string, // Use list_id as fallback name since we don't have the actual name here
                  },
                  scope,
                }
                continue
              }

              // Handle buy-universe-specific fields that aren't in UniverseApiField
              if (constraint.type === ModelConstraintType.BuyUniverse) {
                if (field === 'order_ytw') {
                  const existingValue = model.ytwRange as number[]
                  if (operator === Operator.LessThanOrEqualTo) {
                    model.ytwRange = [existingValue[0] ?? 0, Number(value)]
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    model.ytwRange = [Number(value), existingValue[1] ?? 0]
                  }
                  continue
                }
                if (field === 'order_price') {
                  const existingValue = model.priceRange as number[]
                  if (operator === Operator.LessThanOrEqualTo) {
                    model.priceRange = [existingValue[0] ?? 0, Number(value)]
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    model.priceRange = [Number(value), existingValue[1] ?? 0]
                  }
                  continue
                }
              }

              const universeApiField = field as UniverseApiField
              switch (universeApiField) {
                case UniverseApiField.Type:
                  if (operator === 'in') {
                    model.assetClasses = value as BondType[]
                  } else if (operator === 'equal_to') {
                    model.assetClasses = [value as BondType]
                  }
                  break
                case UniverseApiField.MunicipalState:
                  if (typeof value === 'string') {
                    model.municipalState = value
                  } else {
                    model.excludeMunicipalStates = value as string[]
                  }
                  break
                case UniverseApiField.MunicipalStatePreference:
                  break
                case UniverseApiField.Coupon:
                case UniverseApiField.IssueSize:
                case UniverseApiField.LiquidityInstitutionalAggregate:
                case UniverseApiField.CloseYtw:
                case UniverseApiField.ClosePrice:
                case UniverseApiField.Duration:
                case UniverseApiField.MaturityInYears:
                case UniverseApiField.NextCallInYears:
                case UniverseApiField.WorkoutInYears: {
                  const universeKey = API_FIELD_TO_UNIVERSE_KEYS[
                    universeApiField
                  ] as Extract<UniverseKeys, SliderKeys>
                  const existingValue = model[universeKey] as number[]
                  if (operator === Operator.LessThanOrEqualTo) {
                    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                    model[universeKey] = [existingValue[0]!, Number(value)]
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                    model[universeKey] = [Number(value), existingValue[1]!]
                  }
                  break
                }
                case UniverseApiField.CouponType:
                  model.couponType = value as CouponType[]
                  break
                case UniverseApiField.TreasurySubtype:
                  model.treasurySubtype = value as TreasurySubtype[]
                  break
                case UniverseApiField.UseOfProceeds:
                  model.excludeUseOfProceeds = value as string[]
                  break
                case UniverseApiField.Sector:
                  model.excludeSectors = value as string[]
                  break
                case UniverseApiField.Issuer:
                  model.excludeIssuers = value as string[]
                  break
                case UniverseApiField.SpRating:
                  if (operator === Operator.LessThanOrEqualTo) {
                    model.maxSpRange = value.toString()
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    model.minSpRange = value.toString()
                  }
                  // Determine the rating mode after setting the values
                  if (
                    model.minSpRange === 'BBB-' &&
                    model.maxSpRange === 'AAA'
                  ) {
                    model.spRatingMode = RatingMode.Investment
                  } else if (
                    model.minSpRange === 'D' &&
                    model.maxSpRange === 'BB+'
                  ) {
                    model.spRatingMode = RatingMode.HighYield
                  } else if (model.minSpRange || model.maxSpRange) {
                    model.spRatingMode = RatingMode.Custom
                  } else {
                    model.spRatingMode = RatingMode.None
                  }
                  break
                case UniverseApiField.LowestRating:
                  if (operator === Operator.LessThanOrEqualTo) {
                    model.maxLowestRatingRange = value.toString()
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    model.minLowestRatingRange = value.toString()
                  }
                  // Determine the rating mode after setting the values
                  if (
                    model.minLowestRatingRange === 'BBB-' &&
                    model.maxLowestRatingRange === 'AAA'
                  ) {
                    model.lowestRatingMode = RatingMode.Investment
                  } else if (
                    model.minLowestRatingRange === 'D' &&
                    model.maxLowestRatingRange === 'BB+'
                  ) {
                    model.lowestRatingMode = RatingMode.HighYield
                  } else if (
                    model.minLowestRatingRange ||
                    model.maxLowestRatingRange
                  ) {
                    model.lowestRatingMode = RatingMode.Custom
                  } else {
                    model.lowestRatingMode = RatingMode.None
                  }
                  break
                case UniverseApiField.MoodysRating:
                  if (operator === Operator.LessThanOrEqualTo) {
                    model.maxMoodysRange = value.toString()
                  }
                  if (operator === Operator.GreaterThanOrEqualTo) {
                    model.minMoodysRange = value.toString()
                  }
                  // Determine the rating mode after setting the values
                  if (
                    model.minMoodysRange === 'Baa3' &&
                    model.maxMoodysRange === 'Aaa'
                  ) {
                    model.moodysRatingMode = RatingMode.Investment
                  } else if (
                    model.minMoodysRange === 'C' &&
                    model.maxMoodysRange === 'Ba1'
                  ) {
                    model.moodysRatingMode = RatingMode.HighYield
                  } else if (model.minMoodysRange || model.maxMoodysRange) {
                    model.moodysRatingMode = RatingMode.Custom
                  } else {
                    model.moodysRatingMode = RatingMode.None
                  }
                  break
                case UniverseApiField.MunicipalTaxableFederal:
                case UniverseApiField.IsAmtApplicable:
                case UniverseApiField.PrivatePlacement:
                case UniverseApiField.RegS:
                case UniverseApiField.BelowDeMinimisThreshold:
                case UniverseApiField.FdicInsured: {
                  const universeKeyForAllowEnumFields =
                    API_FIELD_TO_UNIVERSE_KEYS[universeApiField] as Extract<
                      UniverseKeys,
                      | 'municipalTaxableFederal'
                      | 'amtApplicable'
                      | 'privatePlacement'
                      | 'regulationS'
                      | 'belowDeMinimisThreshold'
                      | 'fdicInsured'
                    >
                  const reduxValue =
                    value === undefined
                      ? [AllowEnum.Allow]
                      : [AllowEnum.DontAllow]
                  model[universeKeyForAllowEnumFields] = reduxValue as [string]
                  break
                }
                case UniverseApiField.IncludeTickers:
                case UniverseApiField.ExcludeTickers:
                  if (operator === Operator.In) {
                    model.includeTickers = value as string[]
                  } else if (operator === Operator.NotIn) {
                    model.excludeTickers = value as string[]
                  }
                  break
                case UniverseApiField.IncludeIsins:
                case UniverseApiField.IncludeCusips:
                case UniverseApiField.IncludeInstruments:
                  model.includeInstruments = value as string[]
                  break
                case UniverseApiField.ExcludeStructures:
                  model.excludeStructures = value as (
                    | 'perpetual'
                    | 'sinking'
                    | 'callable'
                    | 'bullet'
                  )[]
                  break
                case UniverseApiField.Perpetual:
                  model.excludeStructures = [
                    ...model.excludeStructures,
                    'perpetual',
                  ]
                  break
                case UniverseApiField.SinkingFund:
                  model.excludeStructures = [
                    ...model.excludeStructures,
                    'sinking',
                  ]
                  break
                case UniverseApiField.Callable:
                  model.excludeStructures = [
                    ...model.excludeStructures,
                    'callable',
                  ]
                  break
                case UniverseApiField.Bullet:
                  model.excludeStructures = [
                    ...model.excludeStructures,
                    'bullet',
                  ]
                  break
                case UniverseApiField.Currency:
                  model.includeCurrencies = value as string[]
                  break
                case UniverseApiField.CountryDomicile:
                  model.includeCountryDomicile = value as string[]
                  break
                case UniverseApiField.GicsSectorL4:
                  model.excludeGicsSectorL4 = value as string[]
                  break
                case UniverseApiField.IceSectorL4:
                  model.excludeIceSectorL4 = value as string[]
                  break
                case UniverseApiField.VrdoIndicator: {
                  if (operator === 'equal_to' && value === true) {
                    model.vrdoIndicator = [AllowBlockRequireEnum.Require]
                  } else if (operator === 'not_equal_to' && value === true) {
                    model.vrdoIndicator = [AllowBlockRequireEnum.Block]
                  }
                  break
                }
                case UniverseApiField.IncludeListId:
                case UniverseApiField.ExcludeListId:
                case UniverseApiField.IncludeListField:
                case UniverseApiField.ExcludeListField:
                  break
                default: {
                  const exhaustiveCheck: never = universeApiField
                  LOGGER.warn('Unhandled universe field', {
                    field: exhaustiveCheck,
                  })
                }
              }
            }
          },
        )
        break
      }
      case ModelConstraintType.AverageValue: {
        if (constraint.id && model.constraintIdMap) {
          model.constraintIdMap[`average_value:${constraint.field}`] =
            constraint.id
        }
        const { target_average_value, min_average_value, max_average_value } =
          constraint
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        const target = isNaN(Number(target_average_value))
          ? undefined
          : Number(target_average_value)
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        const min = isNaN(Number(min_average_value))
          ? undefined
          : Number(min_average_value)
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        const max = isNaN(Number(max_average_value))
          ? undefined
          : Number(max_average_value)
        const targetAverageApiField = constraint.field as TargetAverageApiField
        switch (targetAverageApiField) {
          // TODO: In the future, we should think about how to generalize these cases
          // to avoid bugs. We basically need to do the opposite of createTargetBandConstraint
          case TargetAverageApiField.SpRating: {
            const band = {
              target: constraint.target_average_value?.toString() ?? '',
              min: constraint.min_average_value?.toString() ?? '',
              max: constraint.max_average_value?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            model.spRatingTargetBand = { ...band }
            break
          }
          case TargetAverageApiField.LowestRating: {
            const lowestRatingBand = {
              target: constraint.target_average_value?.toString() ?? '',
              min: constraint.min_average_value?.toString() ?? '',
              max: constraint.max_average_value?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            model.lowestRatingTargetBand = { ...lowestRatingBand }
            break
          }
          case TargetAverageApiField.MoodysRating: {
            const moodysRatingBand = {
              target: constraint.target_average_value?.toString() ?? '',
              min: constraint.min_average_value?.toString() ?? '',
              max: constraint.max_average_value?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            model.moodysRatingTargetBand = { ...moodysRatingBand }
            break
          }
          case TargetAverageApiField.Coupon:
            model.couponTargetBand = {
              target: target?.toString() ?? '',
              min: min?.toString() ?? '',
              max: max?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            break
          case TargetAverageApiField.Duration:
            model.durationTargetBand = {
              target: target?.toString() ?? '',
              min: min?.toString() ?? '',
              max: max?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            break
          case TargetAverageApiField.LiquidityScore:
            model.liquidityScoreTargetBand = {
              target: target?.toString() ?? '',
              min: min?.toString() ?? '',
              max: max?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            break
          case TargetAverageApiField.Convexity:
            model.convexityTargetBand = {
              target: target?.toString() ?? '',
              min: min?.toString() ?? '',
              max: max?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            break
          case TargetAverageApiField.Maturity:
            model.maturityTargetBand = {
              target: target?.toString() ?? '',
              min: min?.toString() ?? '',
              max: max?.toString() ?? '',
              mode: TargetBandMode.MinMax,
              buffer: '',
            }
            break
          default: {
            const exhaustiveCheck: never = targetAverageApiField
            LOGGER.warn('Unhandled target average field', {
              field: exhaustiveCheck,
            })
          }
        }
        break
      }
      case ModelConstraintType.Diversification: {
        const {
          field,
          weightRange: [minWeight, maxWeight],
        } = transformDiversificationConstraint(constraint)

        // Create a SimpleDiversificationRange value
        const simpleDiversificationValue = {
          mode: DiversificationMode.Default as const,
          weightRange: [minWeight, maxWeight] as [number, number],
          ...(constraint.id && { id: constraint.id }),
        }

        const diversificationApiField = field as DiversificationApiField
        switch (diversificationApiField) {
          case DiversificationApiField.Cusip:
            model.weightPerBond = simpleDiversificationValue
            break
          case DiversificationApiField.Issuer:
            model.weightPerIssuer = simpleDiversificationValue
            break
          case DiversificationApiField.Sector:
            model.weightPerSector = simpleDiversificationValue
            break
          case DiversificationApiField.MunicipalState:
            model.weightPerState = simpleDiversificationValue
            break
          case DiversificationApiField.MaturityYear:
            model.weightPerMaturityYear = simpleDiversificationValue
            break
          case DiversificationApiField.FdicCode:
            model.weightPerFdicCode = simpleDiversificationValue
            break
          case DiversificationApiField.Insurer:
            model.weightPerInsurer = simpleDiversificationValue
            break
          case DiversificationApiField.UltimateBorrower:
            model.weightPerUltimateBorrower = simpleDiversificationValue
            break
          case DiversificationApiField.UltimateParent:
            model.weightPerUltimateParent = simpleDiversificationValue
            break
          default: {
            const exhaustiveCheck: never = diversificationApiField
            LOGGER.warn('Unhandled diversification field', {
              field: exhaustiveCheck,
            })
          }
        }
        break
      }
      case ModelConstraintType.Piecewise: {
        // Check if this is a diversification piecewise constraint
        const firstPiece = constraint.pieces[0]
        const diversificationPiece = firstPiece?.constraints.find(
          (c) => c.type === ModelConstraintType.Diversification,
        ) as DiversificationConstraint | undefined

        if (diversificationPiece) {
          const piecewiseField =
            diversificationPiece.field as DiversificationApiField
          const tiers = transformPiecewiseToDiversificationTiers(constraint)

          const tieredValue = {
            mode: DiversificationMode.Tiered as const,
            tiers,
            ...(constraint.id && { id: constraint.id }),
          }

          // Map field to model property
          switch (piecewiseField) {
            case DiversificationApiField.Cusip:
              model.weightPerBond = tieredValue
              break
            case DiversificationApiField.Issuer:
              model.weightPerIssuer = tieredValue
              break
            case DiversificationApiField.Sector:
              model.weightPerSector = tieredValue
              break
            case DiversificationApiField.MunicipalState:
              model.weightPerState = tieredValue
              break
            case DiversificationApiField.MaturityYear:
              model.weightPerMaturityYear = tieredValue
              break
            case DiversificationApiField.FdicCode:
              model.weightPerFdicCode = tieredValue
              break
            case DiversificationApiField.Insurer:
              model.weightPerInsurer = tieredValue
              break
            case DiversificationApiField.UltimateBorrower:
              model.weightPerUltimateBorrower = tieredValue
              break
            case DiversificationApiField.UltimateParent:
              model.weightPerUltimateParent = tieredValue
              break
            default: {
              const exhaustiveCheck: never = piecewiseField
              LOGGER.warn('Unhandled diversification field', {
                field: exhaustiveCheck,
              })
            }
          }
        }
        break
      }
      case ModelConstraintType.Allocation: {
        const filterCondition = constraint.filter as FilterCondition
        if (filterCondition.municipal_state) {
          // Since state focused ladder policies are actually a type of allocation constraint,
          // we need to set the municipal state preference and state here.
          model.municipalStatePreference =
            MunicipalStatePreference.StatePreferred
          model.municipalState = filterCondition.municipal_state
            .equal_to as string
          model.stateMinWeight = constraint.min_weight
            ? constraint.min_weight * 100
            : null
          model.stateTargetWeight = constraint.target_weight
            ? constraint.target_weight * 100
            : null
          model.stateMaxWeight = constraint.max_weight
            ? constraint.max_weight * 100
            : null
          model.showMinMax = true
          if (constraint.id && model.constraintIdMap) {
            model.constraintIdMap.allocation_state = constraint.id
          }
        } else {
          const validatedAllocations = transformAllocationConstraint(constraint)
          if (validatedAllocations.length > 0) {
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            model.allocationConstraints!.push(...validatedAllocations)
          }
        }
        break
      }
    }
  })

  return model as ModelBuilder
}

export const useModelData = () => {
  return useTieredSelector((state) => state.modelBuilder?.model) as ModelBuilder
}

const targetBandSchema = z
  .object({
    target: z.union([
      z.number(),
      z.null(),
      z
        .string()
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        .refine((val) => !isNaN(Number(val)), {
          message: 'Target must be a valid number',
        }),
    ]),
    min: z.union([
      z.number(),
      z.null(),
      z
        .string()
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        .refine((val) => !isNaN(Number(val)), {
          message: 'Min must be a valid number',
        }),
    ]),
    max: z.union([
      z.number(),
      z.null(),
      z
        .string()
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        .refine((val) => !isNaN(Number(val)), {
          message: 'Max must be a valid number',
        }),
    ]),
    mode: z.nativeEnum(TargetBandMode),
    buffer: z.union([z.number(), z.string(), z.null()]),
  })
  .refine(
    (data) => {
      if (typeof data.target === 'number' && typeof data.min === 'number') {
        return data.min <= data.target
      }
      return true
    },
    {
      message: 'Min must be less than or equal to target',
    },
  )
  .refine(
    (data) => {
      if (typeof data.target === 'number' && typeof data.max === 'number') {
        return data.target <= data.max
      }
      return true
    },
    {
      message: 'Target must be less than or equal to max',
    },
  )
  .refine(
    (data) => {
      if (typeof data.min === 'number' && typeof data.max === 'number') {
        return data.min <= data.max
      }
      return true
    },
    {
      message: 'Min must be less than or equal to max',
    },
  )

const allocationConditionSchema = z.object({
  field: z.string() as z.ZodType<ReferenceDataField | null>,
  operator: z.string() as z.ZodType<Operator | null>,
  inputValue: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
  ]) as z.ZodType<string | number | string[] | null>,
})

const allocationConstraintSchema = z.object({
  id: z.string().optional(),
  targetBand: targetBandSchema,
  conditions: z.array(allocationConditionSchema),
})

/**
 * Removes the keys 'or' and 'and' from the filter to get the constraints
 * @param field - The field to check
 * @returns True if the field is not 'or' or 'and', false otherwise
 */
export const removeOrAndKeysFromFilter = (
  field: string | [string, unknown],
): boolean => {
  const fieldName = Array.isArray(field) ? field[0] : field
  return ![
    CompoundExpressionOperator.Or.toString(),
    CompoundExpressionOperator.And.toString(),
  ].includes(fieldName)
}

function transformAllocationConstraint(
  constraint: AllocationConstraint,
): AllocationConstraintState[] {
  const {
    filter: allocationFilter,
    target_weight,
    min_weight,
    max_weight,
  } = constraint

  const allocations: AllocationConstraintState[] = []
  const uiConditions: FilterBuilderResponseUnion[] = []

  if (allocationFilter && typeof allocationFilter === 'object') {
    Object.entries(allocationFilter).forEach(([fieldKey, opsAndValuesMap]) => {
      if (typeof opsAndValuesMap !== 'object' || opsAndValuesMap === null) {
        return
      }
      Object.entries(opsAndValuesMap).forEach(
        ([operatorString, inputValue]) => {
          const uiCondition = {
            field: fieldKey as ReferenceDataField | BuyUniverseFieldKeys,
            operator: operatorString as Operator,
            inputValue: inputValue as FilterValue | string | null,
          }
          uiConditions.push(uiCondition as FilterBuilderResponseUnion)
        },
      )
    })
  }

  const allocationConstraintForUIState = {
    ...(constraint.id && { id: constraint.id }),
    targetBand: {
      target: target_weight ? decimalToPercentage(target_weight) : null,
      min: min_weight ? decimalToPercentage(min_weight) : null,
      max: max_weight ? decimalToPercentage(max_weight) : null,
      mode: TargetBandMode.MinMax,
      buffer: '',
    },
    conditions: uiConditions,
  }

  const check = allocationConstraintSchema.safeParse(
    allocationConstraintForUIState,
  )
  if (check.success) {
    allocations.push(check.data as AllocationConstraintState)
  } else {
    LOGGER.error(
      'Failed to parse allocation constraint for UI state after transformation',
      {
        error: new Error(
          'Failed to parse allocation constraint for UI state after transformation',
        ),
        errorDetails: check.error.format(),
        originalFilter: allocationFilter,
        transformedState: allocationConstraintForUIState,
      },
    )
  }
  return allocations
}

const diversificationConstraintSchema = z.object({
  field: z.enum(
    Object.values(DiversificationApiField) as [string, ...string[]],
  ),
  weightRange: z
    .tuple([z.number(), z.number()])
    .refine(([min, max]) => min < max, {
      message: 'Min weight must be less than max weight',
    }),
})

type DiversificationConstraintState = z.infer<
  typeof diversificationConstraintSchema
>

function transformDiversificationConstraint(
  constraint: DiversificationConstraint,
): DiversificationConstraintState {
  const {
    field,
    min_weight_per_unique_value,
    max_weight_per_unique_value,
    min_market_value_per_unique_value,
    max_market_value_per_unique_value,
  } = constraint

  // Get the appropriate default values based on the field
  const diversificationApiField = field as DiversificationApiField
  let defaultRange: [number, number]
  switch (diversificationApiField) {
    case DiversificationApiField.Issuer:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerIssuer
        .defaultValue as [number, number]
      break
    case DiversificationApiField.Sector:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerSector
        .defaultValue as [number, number]
      break
    case DiversificationApiField.Cusip:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerBond
        .defaultValue as [number, number]
      break
    case DiversificationApiField.MunicipalState:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerState
        .defaultValue as [number, number]
      break
    case DiversificationApiField.MaturityYear:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerMaturityYear
        .defaultValue as [number, number]
      break
    case DiversificationApiField.FdicCode:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerFdicCode
        .defaultValue as [number, number]
      break
    case DiversificationApiField.Insurer:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerInsurer
        .defaultValue as [number, number]
      break
    case DiversificationApiField.UltimateBorrower:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateBorrower
        .defaultValue as [number, number]
      break
    case DiversificationApiField.UltimateParent:
      defaultRange = DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateParent
        .defaultValue as [number, number]
      break
    default: {
      const exhaustiveCheck: never = diversificationApiField
      LOGGER.warn('Unhandled diversification field', { field: exhaustiveCheck })
      return {} as DiversificationConstraintState
    }
  }

  const [defaultMin, defaultMax] = defaultRange
  const isFdicField =
    diversificationApiField === DiversificationApiField.FdicCode

  let minWeight: number
  let maxWeight: number

  if (isFdicField) {
    // For FDIC, use market value fields directly (no percentage conversion)
    minWeight = min_market_value_per_unique_value ?? defaultMin
    maxWeight = max_market_value_per_unique_value ?? defaultMax
  } else {
    // For other fields, use weight fields with percentage conversion
    minWeight = min_weight_per_unique_value
      ? min_weight_per_unique_value * 100
      : defaultMin
    maxWeight = max_weight_per_unique_value
      ? max_weight_per_unique_value * 100
      : defaultMax
  }

  const result = diversificationConstraintSchema.safeParse({
    field,
    weightRange: [minWeight, maxWeight] as [number, number],
  })

  if (result.success) {
    return result.data
  }

  return {
    field: field as DiversificationKeys,
    weightRange: defaultRange,
  }
}

/**
 * Transform a PiecewiseConstraint into an array of TieredDiversificationTier
 */
function transformPiecewiseToDiversificationTiers(
  constraint: PiecewiseConstraint,
): TieredDiversificationTier[] {
  return constraint.pieces.map((piece, index) => {
    const isLastTier = index === constraint.pieces.length - 1
    const diversificationConstraint = piece.constraints.find(
      (c) => c.type === ModelConstraintType.Diversification,
    ) as DiversificationConstraint | undefined

    if (!diversificationConstraint) {
      // Return default tier if no diversification constraint found
      return {
        id: crypto.randomUUID(),
        maxMarketValue: isLastTier ? null : 0,
        weightRange: [0, 100] as [number, number],
      }
    }

    const { weightRange } = transformDiversificationConstraint(
      diversificationConstraint,
    )

    return {
      id: crypto.randomUUID(),
      maxMarketValue: isLastTier
        ? null
        : ((piece.condition.account_market_value?.less_than_or_equal_to ??
            null) as number | null),
      weightRange: weightRange as [number, number],
    }
  })
}

// fieldsToOmit identifies the fields that are not applicable for the given asset classes
// This is based on an intersection of the fields that are non-applicable for each asset class
// (alternatively, consider this as a union of the fields that are applicable for each asset class)
export function fieldsToOmit(assetClasses: string[]): string[] {
  if (assetClasses.length === 0) {
    return []
  }
  // For multiple asset classes, find intersection (fields that are non-applicable for ALL)
  const allFieldsSets = assetClasses.map((value) => {
    const universeFields = nonApplicableUniverseFields[value as BondType]
    const diversificationFields =
      nonApplicableDiversificationFields[value as BondType]
    const targetAverageFields =
      nonApplicableTargetAverageFields[value as BondType]
    return new Set([
      ...universeFields,
      ...diversificationFields,
      ...targetAverageFields,
    ])
  })

  // Find intersection: fields that appear in ALL sets
  const intersection = new Set<string>()
  const firstSet = allFieldsSets[0]

  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  for (const field of firstSet!) {
    if (allFieldsSets.every((set) => set.has(field))) {
      intersection.add(field)
    }
  }

  return Array.from(intersection)
}

/**
 * Determines which asset classes a field is applicable to
 * @param field - The field name (ModelBuilder key or API field name)
 * @param fieldType - The type of field (universe, diversification, or targetAverage)
 * @returns Array of BondType values that the field is applicable to
 */
function getApplicableAssetClassesForField(
  field: string,
  fieldType: 'universe' | 'diversification' | 'targetAverage',
): BondType[] {
  const allAssetClasses = Object.values(BondType)
  const applicableAssetClasses: BondType[] = []

  for (const assetClass of allAssetClasses) {
    let isNonApplicable = false

    switch (fieldType) {
      case 'universe': {
        const nonApplicableFields = nonApplicableUniverseFields[assetClass]
        isNonApplicable = nonApplicableFields.includes(field as UniverseKeys)
        break
      }
      case 'diversification': {
        const nonApplicableFields =
          nonApplicableDiversificationFields[assetClass]
        isNonApplicable = nonApplicableFields.includes(
          field as DiversificationKeys,
        )
        break
      }
      case 'targetAverage': {
        const nonApplicableFields = nonApplicableTargetAverageFields[assetClass]
        isNonApplicable = nonApplicableFields.includes(
          field as TargetAverageKeys,
        )
        break
      }
    }

    if (!isNonApplicable) {
      applicableAssetClasses.push(assetClass)
    }
  }

  return applicableAssetClasses
}

/**
 * Determines if a scope should be added for a field based on its applicable asset classes
 * @param field - The field name (ModelBuilder key)
 * @param fieldType - The type of field (universe, diversification, or targetAverage)
 * @param allAssetClasses - All asset classes in the model
 * @returns FilterObject scope if the field is not applicable to all asset classes, undefined otherwise
 */
function getScopeForField(
  field: string,
  fieldType: 'universe' | 'diversification' | 'targetAverage',
  allAssetClasses: BondType[],
): FilterObject | undefined {
  if (allAssetClasses.length === 0) {
    return undefined
  }

  const applicableAssetClasses = getApplicableAssetClassesForField(
    field,
    fieldType,
  )

  // If field is applicable to all asset classes in the model, no scope needed
  const applicableSet = new Set(applicableAssetClasses)
  const modelSet = new Set(allAssetClasses)
  const isApplicableToAll = Array.from(modelSet).every((ac) =>
    applicableSet.has(ac),
  )

  if (isApplicableToAll) {
    return undefined
  }

  // Find intersection of applicable asset classes and model asset classes
  const intersection = applicableAssetClasses.filter((ac) =>
    allAssetClasses.includes(ac),
  )

  if (intersection.length === 0) {
    // Field is not applicable to any asset class in the model - this shouldn't happen
    // but return undefined to avoid creating invalid constraints
    return undefined
  }

  // If intersection is same as all asset classes, no scope needed
  if (intersection.length === allAssetClasses.length) {
    return undefined
  }

  // Return scope with intersection of applicable asset classes
  return {
    type: {
      in: intersection,
    },
  } as FilterObject
}
